// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * UitvaartDossierPage - /dossier/uitvaart
 * Praktisch dossier voor de uitvaartverzorger.
 * Bevat GEEN medische, financiële, notariële of digitale gegevens.
 * V40B-P1
 */
import { useState, useEffect } from 'react'
import { useNavigate }    from 'react-router-dom'
import { Flower2, Download, Copy, Mail, CheckCircle2, ArrowLeft, AlertTriangle, MessageSquare } from 'lucide-react'
import type { ExportData, ExportSection } from '@/lib/export/wilsappExport'
import { Button }         from '@/components/ui/Button'
import { useAuthStore, useWizardStore } from '@/store'
import { getFirstProfile, getProfileAddress } from '@/services/profile'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { loadPartner, loadPets, wizardLoad } from '@/services/wizard'
import { loadFuneralWishes, loadDocuments, loadMessages } from '@/services/wishes'
import { DOSSIER_OVERVIEW } from '@/lib/constants/routes'

// ── Hulpfuncties ──────────────────────────────────────────────────────────
function nl(v: string | undefined | null) { return v?.trim() || '' }
// Eén item-regel met statusicoon. Voorvertoning, PDF en DOCX delen dezelfde
// gegevens; uitsluitend de opmaak verschilt.
function itemLine(label: string, value: string): string {
  const v = nl(value)
  return v ? `  ✅ ${label}: ${v}` : `  ○  ${label}: niet ingevuld`
}
// Serialiseert exportsecties naar tekst voor de voorvertoning, zodat die exact
// dezelfde informatie bevat als de PDF- en DOCX-export (single source of truth).
function serializeSections(sections: ExportSection[]): string {
  return sections.map(s => {
    const body = s.items.length
      ? s.items.map(it => itemLine(it.label, it.value)).join('\n')
      : '  ○  (geen gegevens ingevuld)'
    return ['', s.title, '─'.repeat(Math.min(s.title.length, 52)), body].join('\n')
  }).join('\n')
}
function datum(): string {
  return new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
// Instanties vs. persoonlijke berichten - detectie op recipientLabel (geen schemawijziging).
const PRAKTISCHE_LABELS = ['werkgever', 'bank', 'verzekeraar', 'pensioenfonds', 'zorgverzekering', 'belasting', 'gemeente', 'notaris', 'uitvaart']
function isInstantieBericht(label: string): boolean {
  const l = (label || '').toLowerCase()
  return PRAKTISCHE_LABELS.some(p => l.includes(p))
}

interface Punt { label: string; reden: string }

export function UitvaartDossierPage() {
  const navigate      = useNavigate()
  const { userId }    = useAuthStore()
  const { answers }   = useWizardStore()

  const [isLoading,    setIsLoading]    = useState(true)
  const [dossierText,  setDossierText]  = useState('')
  const [exportData,    setExportData]    = useState<ExportData | null>(null)
  const [firstName,    setFirstName]    = useState('')
  const [copied,       setCopied]       = useState(false)
  const [whatsappText, setWhatsappText] = useState('')
  const [checklist,    setChecklist]    = useState<{ label: string; warning?: string }[]>([])
  const [showWarning,  setShowWarning]  = useState(false)
  const [pendingAction,setPendingAction]= useState<'download'|'email'|'copy'|null>(null)

  useEffect(() => {
    if (!userId) return
    async function load() {
      const [
        profile, profileAddr, vpList,
        partner, pets, funeral, documents, fcRaw, messages,
      ] = await Promise.all([
        getFirstProfile(),
        getProfileAddress(userId!),
        loadTrustedPersons(userId!),
        loadPartner(userId!),
        loadPets(userId!),
        loadFuneralWishes(userId!),
        loadDocuments(userId!),
        wizardLoad<Record<string,string>>(userId!, 'funeralCosts'),
        loadMessages(userId!),
      ])
      const name = profile?.firstName || '-'
      setFirstName(profile?.firstName || '')
      const fc = fcRaw as {
        hasInsurance?: string; insurer?: string; insurerOther?: string
        policyNumber?: string; insuredAmount?: string; docsLocation?: string
        financialContactName?: string; financialContactRole?: string
        financialContactPhone?: string; financialContactEmail?: string
        financialContactInstructions?: string; financialContactCustom?: string
      } | null

      const activeVPs = (vpList || []).filter(v => v.confirmationStatus !== 'REVOKED')
      const primaryVP = activeVPs.find(v => v.role === 'PRIMARY')

      // ── Open aandachtspunten ─────────────────────────────────────────────
      const punten: Punt[] = []
      if (!funeral?.type)
        punten.push({ label: 'Type uitvaart niet ingevuld', reden: 'De uitvaartverzorger heeft dit nodig om de wensen te kunnen uitvoeren.' })
      if (!primaryVP)
        punten.push({ label: 'Vertrouwenspersoon niet ingevuld', reden: 'Wie is het eerste aanspreekpunt voor de uitvaartverzorger?' })
      if (!fc?.hasInsurance || fc.hasInsurance === '')
        punten.push({ label: 'Uitvaartverzekering niet beantwoord', reden: 'De uitvaartverzorger moet weten hoe de uitvaart wordt betaald.' })
      if (!fc?.financialContactName && !fc?.financialContactCustom)
        punten.push({ label: 'Financieel contactpersoon niet ingevuld', reden: 'Wie regelt de financiële afwikkeling na overlijden?' })

      // ── H1 - Gegevens overledene ────────────────────────────────────────
      const adres = [profileAddr?.street, profileAddr?.houseNumber,
        profileAddr?.houseNumberAddition].filter(Boolean).join(' ')
      const pc    = [profileAddr?.postalCode, profileAddr?.city].filter(Boolean).join(' ')

      // ── Labels ──────────────────────────────────────────────────────────
      const typeLabel = funeral?.type === 'burial' ? 'Begraving'
        : funeral?.type === 'cremation' ? 'Crematie'
        : funeral?.type === 'natural'   ? 'Natuurbegraving' : ''
      const ceremLabel = funeral?.ceremonyType === 'religious' ? 'Religieus'
        : funeral?.ceremonyType === 'civil'    ? 'Burgerlijk'
        : funeral?.ceremonyType === 'private'  ? 'Privé'
        : funeral?.ceremonyType === 'none'     ? 'Geen ceremonie'
        : funeral?.ceremonyType               || ''
      const insurerName = fc?.insurer === 'Anders' ? (fc.insurerOther || '') : (fc?.insurer || '')
      const insuranceStatus = fc?.hasInsurance === 'yes' ? 'Ja'
        : fc?.hasInsurance === 'no' ? 'Nee' : 'Niet beantwoord'
      const fcNaam = fc?.financialContactName || fc?.financialContactCustom || ''
      const partnerNaam = partner?.hasPartner === 'yes' && partner.name
        ? [partner.name, partner.lastName].filter(Boolean).join(' ') : ''

      // ── H7 - relevante documentlocaties ─────────────────────────────────
      const uitvaartDocs = (documents || []).filter(d =>
        ['uitvaart','verzekering','identiteit','overlijden','polis'].some(
          kw => (d.category + ' ' + d.label).toLowerCase().includes(kw)
        )
      )

      // ── H8 - Huisdieren COMPACT (alleen wat relevant is voor de eerste opvang) ──
      // Uitgebreide huisdiergegevens horen in het Nabestaandendossier, niet hier.
      const heeftHuisdieren = pets?.hasPets === 'yes' && (pets.pets?.length ?? 0) > 0
      const petList = heeftHuisdieren && pets?.pets ? pets.pets : []
      const verzorgers = petList.map(p => nl(p.caretaker)).filter(Boolean)
      const verzorgingGeregeld = !heeftHuisdieren ? ''
        : petList.every(p => nl(p.caretaker)) ? 'Ja'
        : verzorgers.length ? 'Gedeeltelijk' : 'Nee'
      const huisdierContact = verzorgers[0] || ''

      // ── Berichtenservice (uitsluitend tellingen - geen berichtinhoud) ──────
      const berichten     = messages || []
      const persoonlijk   = berichten.filter(m => !isInstantieBericht(m.recipientLabel))
      const instanties    = berichten.filter(m =>  isInstantieBericht(m.recipientLabel))
      const berichtenKlaar = berichten.length > 0

      // ── Eén bron voor voorvertoning, PDF en DOCX ────────────────────────
      const exportSections: ExportSection[] = [
        { title: 'HOOFDSTUK 1 - GEGEVENS OVERLEDENE', items: [
          { label: 'Naam',                value: name },
          { label: 'Adres',               value: adres },
          { label: 'Postcode/Woonplaats', value: pc },
        ]},
        { title: 'HOOFDSTUK 2 - EERSTE CONTACTPERSOON', items: primaryVP ? [
          { label: 'Naam',    value: primaryVP.firstName },
          { label: 'Relatie', value: primaryVP.relationship || '' },
          { label: 'E-mail',  value: primaryVP.email || '' },
        ] : [
          { label: 'Eerste contactpersoon', value: '', warn: true },
        ]},
        { title: 'HOOFDSTUK 3 - PARTNER / NABESTAANDE', items: partnerNaam ? [
          { label: 'Naam',     value: partnerNaam },
          { label: 'Telefoon', value: partner?.phone || '' },
          { label: 'E-mail',   value: partner?.email || '' },
        ] : [
          { label: 'Partner', value: 'Geen partner opgegeven' },
        ]},
        { title: 'HOOFDSTUK 4 - UITVAARTWENSEN', items: [
          { label: 'Type uitvaart',      value: typeLabel, warn: !funeral?.type },
          { label: 'Locatiewens',        value: nl(funeral?.locationWish) || nl(funeral?.locationVenue) },
          { label: 'Uitvaartverzorger',  value: nl(funeral?.funeralHome) },
          { label: 'Telefoon verzorger', value: nl(funeral?.funeralHomePhone) },
          { label: 'Contactpersoon',     value: nl(funeral?.funeralHomeContact) },
          { label: 'Ceremonietype',      value: ceremLabel },
          { label: 'Ceremoniewensen',    value: nl(funeral?.ceremonyWishes) },
          { label: 'Muziek',             value: nl(funeral?.music) },
          { label: 'Bloemen',            value: nl(funeral?.flowers) },
          { label: 'Kleding',            value: nl(funeral?.clothing) },
          { label: 'Bijzondere wensen',  value: nl(funeral?.specialRequests) },
        ]},
        { title: 'HOOFDSTUK 5 - UITVAARTVERZEKERING', items: [
          { label: 'Verzekering aanwezig', value: insuranceStatus, warn: insuranceStatus === 'Niet beantwoord' },
          ...(fc?.hasInsurance === 'yes' ? [
            { label: 'Verzekeraar',      value: insurerName },
            { label: 'Polisnummer',      value: fc?.policyNumber || '' },
            { label: 'Verzekerd bedrag', value: fc?.insuredAmount || '' },
            { label: 'Polis bewaard',    value: fc?.docsLocation || '' },
          ] : []),
        ]},
        { title: 'HOOFDSTUK 6 - FINANCIEEL CONTACT', items: fcNaam ? [
          { label: 'Naam',     value: fcNaam },
          { label: 'Functie',  value: fc?.financialContactRole || '' },
          { label: 'Telefoon', value: fc?.financialContactPhone || '' },
          { label: 'E-mail',   value: fc?.financialContactEmail || '' },
          ...(fc?.financialContactInstructions ? [{ label: 'Instructies', value: fc.financialContactInstructions }] : []),
        ] : [
          { label: 'Financieel contactpersoon', value: '', warn: true },
        ]},
        { title: 'HOOFDSTUK 7 - RELEVANTE DOCUMENTLOCATIES', items: uitvaartDocs.length
          ? uitvaartDocs.map(d => ({ label: d.label, value: d.location || 'locatie onbekend' }))
          : [{ label: 'Documenten', value: 'Geen relevante documenten geregistreerd' }]
        },
        { title: 'HOOFDSTUK 8 - HUISDIEREN', items: heeftHuisdieren ? [
          { label: 'Huisdieren aanwezig', value: 'Ja' },
          { label: 'Verzorging geregeld', value: verzorgingGeregeld, warn: verzorgingGeregeld !== 'Ja' },
          ...(huisdierContact ? [{ label: 'Contactpersoon', value: huisdierContact }] : []),
        ] : [
          { label: 'Huisdieren aanwezig', value: 'Nee' },
        ]},
        { title: 'HOOFDSTUK 9 - BERICHTENSERVICE', items: [
          { label: 'Berichten klaar', value: berichtenKlaar ? 'Ja' : 'Nee' },
          ...(berichtenKlaar ? [
            { label: 'Persoonlijke berichten',   value: String(persoonlijk.length) },
            { label: 'Meldingen aan instanties', value: String(instanties.length) },
            { label: 'Status',  value: 'Handmatig verzenden via WilsApp (geen automatische verzending)' },
            { label: 'Actie',   value: 'Vertrouwenspersoon of nabestaande controleert en verzendt de berichten zelf', warn: true },
            { label: 'Toelichting', value: 'In WilsApp staan berichten klaar die na overlijden handmatig kunnen worden verzonden door de vertrouwenspersoon of nabestaande. WilsApp verstuurt in deze pilotversie niet automatisch. Controleer de Berichtenservice in WilsApp voordat familie, instanties of contacten worden geïnformeerd.' },
          ] : []),
        ]},
      ]

      const alertItems   = punten.map(p => `${p.label} - ${p.reden}`)
      const summaryItems = [
        'Neem contact op met een uitvaartverzorger',
        'Controleer de uitvaartverzekering',
        'Informeer de vertrouwenspersoon',
        'Raadpleeg het volledige uitvaartdossier in WilsApp',
      ]

      // ── Voorvertoning-tekst: geserialiseerd uit dezelfde exportSections ──
      const exportDatum = datum()
      const header = [
        '╔══════════════════════════════════════════════════╗',
        '  UITVAARTVERZORGERDOSSIER - WilsApp',
        '╚══════════════════════════════════════════════════╝',
        `  Naam:           ${name}`,
        `  Exportdatum:    ${exportDatum}`,
        `  Documentversie: 1.0`,
        `  Open punten:    ${punten.length}`,
        '',
        '  Dit document is opgesteld door de betrokkene zelf',
        '  en bevat persoonlijke wensen voor de uitvaart.',
        '  Geen medische, financiële of notariële gegevens.',
      ].join('\n')
      const aandachtBlok = ['', 'OPEN AANDACHTSPUNTEN', '─'.repeat(20),
        ...(alertItems.length ? alertItems.map(a => `  ⚠️  ${a}`) : ['  ✅ Geen kritieke open punten'])
      ].join('\n')
      const samenvattingBlok = ['', 'SAMENVATTING VOOR NABESTAANDEN', '─'.repeat(30),
        ...summaryItems.map((s, i) => `  ${i + 1}.  ${s}`)
      ].join('\n')
      const footer = [
        '',
        '═'.repeat(50),
        'Gegenereerd door WilsApp - persoonlijk uitvaartdossier',
        `Exportdatum: ${exportDatum}`,
        'Dit document vervangt geen officiële documenten.',
        '═'.repeat(50),
      ].join('\n')

      const full = [header, serializeSections(exportSections), aandachtBlok, samenvattingBlok, footer].join('\n')
      setDossierText(full)
      setExportData({
        type: 'uitvaart', firstName: name, exportDate: exportDatum, version: '1.0',
        sections: exportSections,
        alertItems,
        summaryItems,
      })

      // ── WhatsApp samenvatting (geen adres/polis/geboortedatum) ──────────
      const waLines = [
        `*Uitvaartwensen - WilsApp samenvatting*`,
        `Naam: ${name}`,
        '',
        primaryVP ? `Eerste contact: ${primaryVP.firstName} (${primaryVP.relationship || 'vertrouwenspersoon'})` : 'Eerste contact: niet ingevuld',
        '',
        typeLabel ? `Type uitvaart: ${typeLabel}` : 'Type uitvaart: niet ingevuld',
        nl(funeral?.locationWish) || nl(funeral?.locationVenue) ? `Locatiewens: ${nl(funeral?.locationWish) || nl(funeral?.locationVenue)}` : '',
        '',
        fc?.hasInsurance === 'yes'     ? `Uitvaartverzekering: Ja (${(fc.insurer === 'Anders' ? fc.insurerOther : fc.insurer) || 'verzekeraar zie dossier'})` :
        fc?.hasInsurance === 'no'      ? 'Uitvaartverzekering: Nee' :
                                         'Uitvaartverzekering: onbekend',
        '',
        fcNaam ? `Financieel contact: ${fcNaam}${fc?.financialContactPhone ? ' - ' + fc.financialContactPhone : ''}` : 'Financieel contact: niet ingevuld',
        '',
        `_Volledig dossier beschikbaar via WilsApp_`,
      ].filter(l => l !== undefined && l !== null).join('\n')
      setWhatsappText(waLines)

      // ── Eerste 24 uur checklist ────────────────────────────────────────
      const cl: { label: string; warning?: string }[] = [
        { label: 'Uitvaartverzorger bellen',       warning: !funeral?.funeralHome ? 'Uitvaartverzorger nog niet ingevuld in WilsApp' : undefined },
        { label: 'Uitvaartverzekering controleren', warning: (!fc?.hasInsurance || fc.hasInsurance === '') ? 'Uitvaartverzekering nog niet beantwoord' : undefined },
        { label: 'Vertrouwenspersoon informeren',   warning: !primaryVP ? 'Geen vertrouwenspersoon ingevuld' : undefined },
        { label: 'Partner / familie informeren',    warning: partner?.hasPartner === 'yes' && !partner.phone ? 'Telefoonnummer partner niet ingevuld' : undefined },
        { label: 'Huisdieren verzorgen',            warning: pets?.hasPets === 'yes' && pets.pets?.some(p => !p.caretaker) ? 'Verzorger voor huisdier(en) ontbreekt' : undefined },
        { label: 'Berichtenservice controleren',    warning: berichtenKlaar ? `${berichten.length} bericht(en) klaar - controleer en verzend handmatig via WilsApp voordat familie of instanties worden geïnformeerd` : undefined },
        { label: 'Belangrijke documenten verzamelen', warning: undefined },
      ]
      setChecklist(cl)
      setIsLoading(false)
    }
    load()
  }, [userId, answers])

  // ── Acties ───────────────────────────────────────────────────────────────
  function triggerWithWarning(action: 'download' | 'email' | 'copy') {
    setPendingAction(action)
    setShowWarning(true)
  }

  function confirmAction() {
    setShowWarning(false)
    // download handled inline
    if (pendingAction === 'email')    doEmail()
    if (pendingAction === 'copy')     handleCopy()
    setPendingAction(null)
  }



  function doEmail() {
    const subject = encodeURIComponent('Uitvaartwensen - persoonlijk dossier WilsApp')
    const body    = encodeURIComponent(dossierText)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }

  function handleWhatsApp() {
    const encoded = encodeURIComponent(whatsappText)
    window.open(`https://wa.me/?text=${encoded}`, '_blank', 'noopener')
  }

  function handleCopy() {
    navigator.clipboard.writeText(dossierText).catch(() => {
      const el = document.createElement('textarea')
      el.value = dossierText
      document.body.appendChild(el); el.select()
      document.execCommand('copy'); document.body.removeChild(el)
    })
    setCopied(true); setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <button onClick={() => navigate(DOSSIER_OVERVIEW)}
        className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors">
        <ArrowLeft className="h-4 w-4" />Terug naar dossiers
      </button>

      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 flex-shrink-0">
          <Flower2 className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink">Uitvaartverzorgerdossier</h1>
          <p className="text-xs text-ink-4">Praktisch overzicht voor de uitvaartverzorger - 9 hoofdstukken</p>
        </div>
      </div>

      {/* Privacy-waarschuwing */}
      <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          <strong className="text-amber-800">Let op:</strong> Dit dossier bevat adresgegevens en
          verzekeringsdetails. Deel dit alleen met vertrouwde personen zoals de uitvaartverzorger.
        </p>
      </div>

      {/* Share-knoppen */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={async () => { if (!exportData) return; const {generateDocx, downloadDocx} = await import('@/lib/export/generateDocx'); downloadDocx(await generateDocx(exportData), `WilsApp-Uitvaartverzorgerdossier-${firstName || 'export'}.docx`) }} icon={<Download className="h-4 w-4" />}>Word (.docx)</Button>
        <Button onClick={async () => { if (!exportData) return; const {generatePdf, downloadPdf} = await import('@/lib/export/generatePdf'); downloadPdf(await generatePdf(exportData), `WilsApp-Uitvaartverzorgerdossier-${firstName || 'export'}.pdf`) }} variant="secondary" icon={<Download className="h-4 w-4" />}>PDF</Button>
        <Button variant="secondary" onClick={() => triggerWithWarning('copy')}
          icon={copied ? <CheckCircle2 className="h-4 w-4 text-sage-600" /> : <Copy className="h-4 w-4" />}>
          {copied ? 'Gekopieerd!' : 'Kopiëren'}
        </Button>
        <Button variant="secondary" onClick={() => triggerWithWarning('email')}
          icon={<Mail className="h-4 w-4" />}>
          E-mailen
        </Button>
        <Button variant="outline" onClick={handleWhatsApp}
          icon={<MessageSquare className="h-4 w-4" />}
          className="text-[#25D366] border-[#25D366] hover:bg-green-50">
          WhatsApp samenvatting
        </Button>
      </div>

      {/* Bevestigingsmodal voor download/e-mail */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowWarning(false)}>
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl px-6 py-5 flex flex-col gap-4"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink mb-1">Controleer vóór delen</p>
                <p className="text-sm text-ink-3 leading-relaxed">
                  Dit dossier bevat adresgegevens en verzekeringsdetails.
                  Deel dit alleen met vertrouwde personen zoals de uitvaartverzorger.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowWarning(false)}
                className="flex-1 rounded-xl border-2 border-sand-300 px-4 py-3 text-sm font-medium text-ink-3 hover:border-sage-300 transition-colors">
                Annuleren
              </button>
              <button onClick={confirmAction}
                className="flex-1 rounded-xl bg-moss text-white px-4 py-3 text-sm font-semibold hover:bg-moss-light transition-colors">
                Doorgaan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eerste 24 uur checklist */}
      {!isLoading && checklist.length > 0 && (
        <div className="rounded-2xl bg-white border border-sand-300 shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-sand-200 bg-rose-50">
            <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider">Eerste 24 uur - aandachtspunten</p>
          </div>
          <div className="px-5 py-4 flex flex-col gap-2">
            {checklist.map((item, i) => (
              <div key={i}>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded border-2 border-sand-300 flex-shrink-0" />
                  <p className="text-sm text-ink">{item.label}</p>
                </div>
                {item.warning && (
                  <p className="text-xs text-amber-600 ml-6 mt-0.5">⚠️ {item.warning}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voorvertoning */}
      {isLoading ? (
        <p className="text-sm text-ink-4">Dossier samenstellen...</p>
      ) : (
        <div className="rounded-2xl bg-white border border-sand-300 shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-sand-200 bg-sand-50">
            <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider">Voorvertoning</p>
          </div>
          <pre className="px-5 py-4 text-xs text-ink leading-relaxed font-mono whitespace-pre-wrap
                         overflow-x-auto max-h-[60vh] overflow-y-auto">
            {dossierText}
          </pre>
        </div>
      )}
    </div>
  )
}
